'use client'
import * as React from 'react'
import { defaultContentLanguage, mapLocaleToContentLanguage } from 'cf-i18n/constants'
import { useInfiniteContentFetchReviewsQuery } from 'codegen/generated/content'
import { useLocale, useTranslations } from 'next-intl'
import { formatDateToLong } from 'utils/date'

import { SingleReview } from './review'
import { ReviewProvider } from './context'

interface Props {
  productId: string
  className?: string
}

export const MAX_REVIEWS = 8

export function Reviews({ productId, className }: Props) {
  const locale = useLocale()
  const t = useTranslations('prod.product_description_page.reviews')
  const contentLanguage = mapLocaleToContentLanguage.get(locale) || defaultContentLanguage
  const {
    data,
    isPending: isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteContentFetchReviewsQuery(
    {
      input: {
        productId: productId ?? '',
        language: contentLanguage,
        limit: MAX_REVIEWS,
      },
    },
    {
      enabled: !!productId,
      initialPageParam: {
        input: {
          productId: productId ?? '',
          language: contentLanguage,
          limit: MAX_REVIEWS,
        },
      },
      getNextPageParam: lastPage => {
        const lastCursor = lastPage.contentProductReviews.cursor ?? undefined
        if (!lastCursor) {
          return
        }
        return {
          input: {
            productId: productId ?? '',
            language: contentLanguage,
            cursor: lastCursor,
            limit: MAX_REVIEWS,
          },
        }
      },
    },
  )

  const reviews = React.useMemo(
    () => data?.pages.flatMap(page => page.contentProductReviews?.reviews),
    [data],
  )

  const totalReviews = data?.pages[0].contentProductReviews.rating.count

  if (isLoading || !reviews?.length) {
    return null
  }

  return (
    <div className={className}>
      <div className="w-full text-[14px] font-semibold">
        {t('pagination', {
          interval: `1  - ${reviews.length}`,
          total: totalReviews ?? 0,
        })}
      </div>
      {reviews.map(review => (
        <ReviewProvider key={review.id} reviewId={review.id}>
          <SingleReview
            user={{
              name:
                review.user.firstName || review.user.lastName
                  ? `${review.user.firstName} ${review.user.lastName}`
                  : `User ${review.user.id}`,
              initials: getInitials(review.user.firstName, review.user.lastName),
              avatarUrl: review.user.avatarUrl || undefined,
              crafterUrl: review.user.profileUrls?.crafter || undefined,
            }}
            rating={review.rating ?? 0}
            date={formatDateToLong(new Date(review.createdAt))}
            reviewText={review.body}
          />
        </ReviewProvider>
      ))}
      <div className="mt-4 text-center">
        {hasNextPage ? (
          // oxlint-disable-next-line react/button-has-type
          <button
            className="rounded-2 text-14 border border-[#DFDDE5] px-4 py-2"
            onClick={() => {
              fetchNextPage()
            }}
          >
            {t('show_more')}
          </button>
        ) : null}
      </div>
    </div>
  )
}

const getInitials = (firstName?: string | null, lastName?: string | null) => {
  if (!firstName || !lastName) {
    return 'U'
  }
  return firstName.charAt(0) + lastName.charAt(0)
}
